import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import AlertMessage from '~/components/data-display/AlertMessage';
import { VStack } from '~/components/layout/Stack';
import ModalActions from '~/components/modals/Modal/ModalActions';
import Text from '~/components/typographies/Text';
import { useChat } from '~/hooks/Chat/IntercomChat';
import { snack } from '~/hooks/ToastProvider';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconSend from '~/icons/icon-send.svg?react';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useCompanyJoinMutation } from '~/services/company';
import { UserRole } from '~/types/company';
import { JSONApiErrorResponse } from '~/types/jsonapi';
import { UserQueryKeys } from '~/types/user';
import { isAxiosError } from '~/utils/helpers';
import { firstJSONApiError } from '~/utils/jsonapi-errors';
import styles from './JoinCompany.module.scss';

interface JoinCompanyProps extends ModalProps {
  taxNumber: string;
}

const JoinCompany: React.FC<JoinCompanyProps> = ({ closeModal, taxNumber }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company.steps.join_company' });
  const queryClient = useQueryClient();
  const companyJoinMutation = useCompanyJoinMutation();
  const { captureException } = useExceptionLogger();
  const { show, open } = useChat();
  const { track } = useAnalytics();

  const joinCompany = async () => {
    try {
      await companyJoinMutation.mutateAsync({
        userRole: UserRole.EMPLOYEE,
        taxNumber,
      });
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      closeModal();
      snack.success(t('toast.success'));
    } catch (error) {
      captureException(error, {
        extra: {
          tax_number: taxNumber,
        },
      });
      if (
        isAxiosError<JSONApiErrorResponse>(error) &&
        error.response?.status === 422 &&
        firstJSONApiError(error)?.detail === 'has already been taken'
      ) {
        snack.error(t('toast.already_exist_error'));
      } else {
        snack.error(t('toast.generic_error'));
      }
    }
  };

  return (
    <VStack gap={5}>
      <Text variant="body2" weight="regular">
        <Trans t={t} i18nKey="description" />
      </Text>
      <AlertMessage type="info" box className={styles.alert}>
        <Trans
          t={t}
          i18nKey="alert_info"
          components={{
            u: (
              <a
                onClick={(e) => {
                  e.stopPropagation();
                  show();
                  open();
                }}
                target="_blank"
                className={styles.link}
                rel="noreferrer"
              />
            ),
          }}
        />
      </AlertMessage>
      <ModalActions className={styles.actionsContainer}>
        <Button
          variant={'outlined'}
          onClick={() => {
            closeModal();
          }}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => {
            track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
              step: 'join_company_requested',
              metadata: { tax_code: taxNumber },
            });
            joinCompany();
          }}
          Icon={IconSend}
          loading={companyJoinMutation.isPending}
        >
          {t('actions.send_request')}
        </Button>
      </ModalActions>
    </VStack>
  );
};

export default JoinCompany;
